import { useCallback, useEffect, useState } from 'react'

/**
 * Hook del prompt de instalación in-app (PWA).
 *
 * - **Android / Chrome / Edge**: el browser dispara `beforeinstallprompt`; lo
 *   capturamos (`preventDefault` para que no aparezca el mini-infobar nativo) y
 *   exponemos `promptInstall()` para dispararlo desde nuestro propio botón.
 * - **iOS Safari**: NO soporta `beforeinstallprompt` — exponemos `isIOS` para
 *   mostrar instrucciones manuales ("Compartir → Agregar a pantalla de inicio").
 * - Si la app ya está instalada (corriendo en modo standalone) o el usuario
 *   descartó el banner, no se muestra. El descarte se persiste en localStorage.
 *
 * El banner real es `<InstallBanner>`; este hook solo decide cuándo mostrarlo.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISS_KEY = 'tivy.install.dismissed'
// Descartar el banner (X) lo POSPONE este tiempo, no para siempre — así el
// invitado tiene otra oportunidad de instalar más adelante sin que sea molesto.
const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000 // 14 días

function runningStandalone(): boolean {
  if (typeof window === 'undefined') return false
  const mq = window.matchMedia?.('(display-mode: standalone)').matches ?? false
  // iOS Safari expone `navigator.standalone` (no estándar, no tipado en lib.dom).
  const iosStandalone =
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  return mq || iosStandalone
}

function isIOSSafari(): boolean {
  if (typeof window === 'undefined') return false
  const ua = window.navigator.userAgent
  const iOS = /iPhone|iPad|iPod/.test(ua)
  // Chrome/Firefox/Edge en iOS (CriOS/FxiOS/EdgiOS) tampoco soportan A2HS por
  // botón, pero el flujo de "Compartir → Agregar" es de Safari; lo acotamos ahí.
  const safari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua)
  return iOS && safari
}

function wasDismissed(): boolean {
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY)
    if (!raw) return false
    const ts = Number.parseInt(raw, 10)
    if (Number.isNaN(ts)) return false
    return Date.now() - ts < SNOOZE_MS
  } catch {
    return false
  }
}

export interface InstallPromptState {
  /** Mostrar el banner (no instalada, no descartada, instalable o iOS Safari). */
  canShow: boolean
  /** iOS Safari → mostrar instrucciones manuales en vez de botón. */
  isIOS: boolean
  /** Hay un `beforeinstallprompt` capturado → se puede disparar el botón. */
  hasPrompt: boolean
  /** Dispara el prompt nativo (Android/Chrome/Edge). */
  promptInstall: () => Promise<void>
  /** Descarta el banner (persiste en localStorage). */
  dismiss: () => void
}

export function useInstallPrompt(): InstallPromptState {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null)
  const [dismissed, setDismissed] = useState<boolean>(wasDismissed)
  const [installed, setInstalled] = useState<boolean>(runningStandalone)
  const ios = isIOSSafari()

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      e.preventDefault()
      setDeferred(e as BeforeInstallPromptEvent)
    }
    const onInstalled = () => {
      setInstalled(true)
      setDeferred(null)
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  const dismiss = useCallback(() => {
    setDismissed(true)
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()))
    } catch {
      /* storage no disponible — el descarte vale solo para esta sesión */
    }
  }, [])

  const promptInstall = useCallback(async () => {
    if (!deferred) return
    await deferred.prompt()
    const choice = await deferred.userChoice
    setDeferred(null)
    if (choice.outcome === 'accepted') setInstalled(true)
  }, [deferred])

  const canShow = !installed && !dismissed && (deferred !== null || ios)

  return { canShow, isIOS: ios, hasPrompt: deferred !== null, promptInstall, dismiss }
}
